import { useMemo, useState } from "react";
import type { Candidate, Constellation, Query } from "@/api/types";
import { fitToAspect, orient, padBounds, type Point } from "@/lib/constellation";
import { similarityFit, spotBounds, type SimilarityFit } from "@/site/geometry";
import { useSettle } from "@/site/parts";
import { Plate, sharkAspect } from "@/site/plate";

// ------------------------------------------------------------------ confidence readout

function ConfidenceScale({ candidates, top }: { candidates: Candidate[]; top: number }) {
  const W = 400;
  const x = (v: number) => 6 + Math.min(Math.max(v, 0), 1) * (W - 12);
  return (
    <svg viewBox={`0 0 ${W} 44`} className="scale-axis" role="img" aria-label="Confidence scale">
      <line x1={x(0)} y1={20} x2={x(1)} y2={20} stroke="var(--rule-2)" strokeWidth={1} />
      <line x1={x(0)} y1={20} x2={x(top)} y2={20} stroke="var(--signal)" strokeWidth={3} />
      {Array.from({ length: 11 }, (_, i) => (
        <line
          key={i}
          x1={x(i / 10)}
          y1={i % 5 === 0 ? 12 : 16}
          x2={x(i / 10)}
          y2={20}
          stroke="var(--ink-3)"
          strokeWidth={1}
        />
      ))}
      {candidates.slice(1).map((c) => (
        <rect
          key={c.shark.id}
          x={x(c.confidence) - 1.5}
          y={22}
          width={3}
          height={8}
          fill="var(--ink)"
        />
      ))}
      <rect x={x(top) - 2} y={8} width={4} height={24} fill="var(--signal)" />
      <text x={x(0)} y={42}>
        0
      </text>
      <text x={x(0.5)} y={42} textAnchor="middle">
        0.5
      </text>
      <text x={x(1)} y={42} textAnchor="end">
        1
      </text>
    </svg>
  );
}

export function Readout({
  candidates,
  label,
  rows,
}: {
  candidates: Candidate[];
  label: string;
  rows: [string, string][];
}) {
  const best = candidates[0];
  const value = useSettle(best?.confidence ?? 0);
  return (
    <div className="readout">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span className="label">{label}</span>
        <span className="label">Calibrated</span>
      </div>
      <p
        className="readout-value"
        aria-label={`${Math.round((best?.confidence ?? 0) * 100)} percent`}
      >
        {(value * 100).toFixed(0)}
        <small>%</small>
      </p>
      <ConfidenceScale candidates={candidates} top={best?.confidence ?? 0} />
      <dl className="kv" style={{ gridTemplateColumns: "1fr auto" }}>
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd className="mono" style={{ textAlign: "right", fontSize: 13 }}>
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ------------------------------------------------------------------ pairs

export interface PairIndex {
  label: number;
  qi: number;
  gi: number;
  query: Point;
  gallery: Point;
}

/** Resolve index pairs to spots, numbered from 1; out-of-range indices are counted, not lost. */
export function indexPairs(q: Constellation, g: Constellation, pairs: [number, number][]) {
  const out: PairIndex[] = [];
  let invalid = 0;
  for (const [qi, gi] of pairs) {
    const a = q.spots[qi];
    const b = g.spots[gi];
    if (!a || !b) {
      invalid++;
      continue;
    }
    out.push({
      label: out.length + 1,
      qi,
      gi,
      query: { x: a[0], y: a[1] },
      gallery: { x: b[0], y: b[1] },
    });
  }
  return { pairs: out, invalid };
}

function RegistrationOverlay({
  q,
  g,
  pairs,
  fit,
  active,
  onActive,
}: {
  q: Constellation;
  g: Constellation;
  pairs: PairIndex[];
  fit: SimilarityFit;
  active: number | null;
  onActive: (label: number | null) => void;
}) {
  const W = 1000;
  const H = 750;
  const frame = fitToAspect(padBounds(spotBounds(g), 0.08), W / H);
  const s = W / frame.width;
  const px = (p: Point) => ({ x: (p.x - frame.x) * s, y: (p.y - frame.y) * s });
  const mapped = q.spots.map(([x, y]) => px(fit.map({ x, y })));
  return (
    <div className="plate plate-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Registration overlay: your spots mapped onto the reference constellation"
      >
        <rect width={W} height={H} fill="var(--panel)" />
        <g stroke="var(--grat)">
          {Array.from({ length: 19 }, (_, i) => (
            <line key={`v${i}`} x1={(i + 1) * 50} y1={0} x2={(i + 1) * 50} y2={H} />
          ))}
          {Array.from({ length: 14 }, (_, i) => (
            <line key={`h${i}`} x1={0} y1={(i + 1) * 50} x2={W} y2={(i + 1) * 50} />
          ))}
        </g>
        {g.spots.map(([x, y], i) => {
          const p = px({ x, y });
          return (
            <circle
              key={`g${i}`}
              cx={p.x}
              cy={p.y}
              r={7}
              fill="none"
              stroke="var(--ink-3)"
              strokeWidth={1.5}
            />
          );
        })}
        {mapped.map((p, i) => (
          <circle key={`q${i}`} cx={p.x} cy={p.y} r={2.6} fill="var(--ink-2)" opacity={0.6} />
        ))}
        {pairs.map((pair) => {
          const a = mapped[pair.qi]!;
          const b = px(pair.gallery);
          const on = pair.label === active;
          return (
            <g
              key={pair.label}
              onMouseEnter={() => onActive(pair.label)}
              onMouseLeave={() => onActive(null)}
              style={{ pointerEvents: "all" }}
            >
              <circle
                cx={b.x}
                cy={b.y}
                r={on ? 14 : 7}
                fill="none"
                stroke="var(--ink)"
                strokeWidth={on ? 2.5 : 1.5}
              />
              <line
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke="var(--signal)"
                strokeWidth={on ? 4 : 2.5}
              />
              <circle cx={a.x} cy={a.y} r={on ? 6 : 4} fill="var(--signal)" />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function Residuals({
  pairs,
  fit,
  active,
  onActive,
}: {
  pairs: PairIndex[];
  fit: SimilarityFit;
  active: number | null;
  onActive: (label: number | null) => void;
}) {
  const order = pairs
    .map((p, i) => ({ label: p.label, r: fit.residuals[i]! }))
    .toSorted((a, b) => a.r - b.r);
  const worst = Math.max(...order.map((o) => o.r), 0.01);
  const max = Math.ceil(worst / 0.05) * 0.05;
  const W = 1000;
  const H = 420;
  const TOP = 40;
  const y = (v: number) => H - (v / max) * (H - TOP);
  const bw = W / Math.max(order.length, 1);
  return (
    <svg
      viewBox={`0 0 ${W} ${H + 40}`}
      role="img"
      aria-label={`Residual per pair, median ${(fit.median * 100).toFixed(1)} percent of pattern radius`}
      className="scale-axis"
    >
      {[0, 0.5, 1].map((t) => (
        <g key={t}>
          <line
            x1={0}
            y1={y(t * max)}
            x2={W}
            y2={y(t * max)}
            stroke="var(--rule)"
            strokeWidth={2}
          />
          {t > 0 && (
            <text x={0} y={y(t * max) - 10} style={{ fontSize: 24 }}>
              {(t * max * 100).toFixed(0)}%
            </text>
          )}
        </g>
      ))}
      {order.map((o, i) => (
        <rect
          key={o.label}
          x={i * bw + bw * 0.12}
          y={y(o.r)}
          width={Math.max(bw * 0.76, 1)}
          height={Math.max(H - y(o.r), 2)}
          fill={o.label === active ? "var(--signal)" : "var(--ink)"}
          onMouseEnter={() => onActive(o.label)}
          onMouseLeave={() => onActive(null)}
        />
      ))}
      <line
        x1={0}
        y1={y(fit.median)}
        x2={W}
        y2={y(fit.median)}
        stroke="var(--signal)"
        strokeWidth={3}
        strokeDasharray="12 8"
      />
      <text
        x={W * 0.46}
        y={y(fit.median) - 10}
        textAnchor="end"
        style={{ fontSize: 24, fill: "var(--signal-ink)" }}
      >
        median
      </text>
      <text x={0} y={H + 32} style={{ fontSize: 24 }}>
        {order.length} pairs, best fit → worst
      </text>
    </svg>
  );
}

function PairTable({
  pairs,
  fit,
  active,
  onActive,
}: {
  pairs: PairIndex[];
  fit: SimilarityFit | null;
  active: number | null;
  onActive: (label: number | null) => void;
}) {
  return (
    <div className="pair-list">
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Query x,y</th>
            <th>Ref x,y</th>
            {fit && <th className="r">Resid.</th>}
          </tr>
        </thead>
        <tbody>
          {pairs.map((p, i) => (
            <tr
              key={p.label}
              data-active={p.label === active}
              onMouseEnter={() => onActive(p.label)}
              onMouseLeave={() => onActive(null)}
            >
              <td>
                <button
                  type="button"
                  onFocus={() => onActive(p.label)}
                  onBlur={() => onActive(null)}
                >
                  {String(p.label).padStart(2, "0")}
                </button>
              </td>
              <td>
                {Math.round(p.query.x)},{Math.round(p.query.y)}
              </td>
              <td>
                {Math.round(p.gallery.x)},{Math.round(p.gallery.y)}
              </td>
              {fit && <td className="r">{(fit.residuals[i]! * 100).toFixed(1)}%</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The whole spot-by-spot case for one candidate: plates, overlay, residuals and the pair list. */
export function Evidence({ query, candidate }: { query: Query; candidate: Candidate }) {
  const [active, setActive] = useState<number | null>(null);
  // Both sides face their view's way, so the pairs, overlay and residuals share that frame.
  const q = useMemo(
    () => (query.constellation ? orient(query.constellation) : null),
    [query.constellation],
  );
  const g = useMemo(() => orient(candidate.image.constellation), [candidate.image.constellation]);
  const resolved = useMemo(
    () => (q ? indexPairs(q, g, candidate.pairs) : null),
    [q, g, candidate.pairs],
  );
  const fit = useMemo(
    () =>
      resolved
        ? similarityFit(resolved.pairs.map((p) => ({ ...p, queryRadius: 0, galleryRadius: 0 })))
        : null,
    [resolved],
  );
  if (!q || !resolved) {
    return (
      <p className="note">
        No fingerprint came back with this query, so there is no spot evidence to show.
      </p>
    );
  }
  const qMarks = new Map(resolved.pairs.map((p) => [p.qi, p.label]));
  const gMarks = new Map(resolved.pairs.map((p) => [p.gi, p.label]));
  const aspect = sharkAspect(q, 1.4, 2.8);
  const shark = candidate.shark;
  return (
    <div className="in-grid evidence-grid">
      <figure style={{ gridColumn: "span 6", margin: 0 }}>
        <div className="fig-head">
          <span className="label">A · Query · your photograph</span>
          <span className="label">{q.spots.length} spots</span>
        </div>
        <Plate
          imageUrl={query.image_url}
          constellation={q}
          aspect={aspect}
          marks={qMarks}
          active={active}
          onActive={setActive}
          label={`Your photograph with ${q.spots.length} detected spots; ${resolved.pairs.length} matched spots ringed`}
        />
      </figure>
      <figure style={{ gridColumn: "span 6", margin: 0 }}>
        <div className="fig-head">
          <span className="label">B · Reference · {shark.id}</span>
          <span className="label">
            {g.spots.length} spots{candidate.image.url ? "" : " · hand-marked"}
          </span>
        </div>
        <Plate
          imageUrl={candidate.image.url}
          constellation={g}
          aspect={aspect}
          marks={gMarks}
          active={active}
          onActive={setActive}
          label={`Reference ${candidate.image.url ? "photograph" : "hand-marked spot chart"} of ${shark.display_name}, matched spots ringed`}
        />
      </figure>
      {fit ? (
        <>
          <figure style={{ gridColumn: "span 5", margin: 0 }}>
            <div className="fig-head">
              <span className="label">C · Registration overlay</span>
              <span className="label">A → B, similarity fit</span>
            </div>
            <RegistrationOverlay
              q={q}
              g={g}
              pairs={resolved.pairs}
              fit={fit}
              active={active}
              onActive={setActive}
            />
            <figcaption className="fig-cap label">
              <span>○ reference spot · ● your spot, registered · — residual</span>
            </figcaption>
          </figure>
          <figure style={{ gridColumn: "span 4", margin: 0 }}>
            <div className="fig-head">
              <span className="label">D · Pair residuals</span>
              <span className="label">% of pattern radius</span>
            </div>
            <dl className="kv" style={{ marginBottom: 16 }}>
              {[
                ["Median residual", `${(fit.median * 100).toFixed(1)}%`],
                ["RMS residual", `${(fit.rms * 100).toFixed(1)}%`],
                ["Scale A → B", `× ${fit.scale.toFixed(3)}`],
                ["Rotation", `${fit.rotationDeg.toFixed(1)}°`],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd className="mono" style={{ textAlign: "right", fontSize: 13 }}>
                    {v}
                  </dd>
                </div>
              ))}
            </dl>
            <Residuals pairs={resolved.pairs} fit={fit} active={active} onActive={setActive} />
          </figure>
        </>
      ) : null}
      <figure style={{ gridColumn: fit ? "span 3" : "span 12", margin: 0 }}>
        <div className="fig-head">
          <span className="label">E · Pairs</span>
          <span className="label">
            n = {resolved.pairs.length}
            {resolved.invalid > 0 ? ` · ${resolved.invalid} unresolved` : ""}
          </span>
        </div>
        <PairTable pairs={resolved.pairs} fit={fit} active={active} onActive={setActive} />
      </figure>
      {fit && (
        <p className="note" style={{ gridColumn: "span 12" }}>
          Residuals are measured here, for display: a least-squares similarity transform (scale,
          rotation, shift) maps your matched spots onto the reference, and each residual is the
          leftover distance as a share of the reference pattern&rsquo;s radius. Perspective and body
          flex add residual the matcher itself tolerates.
        </p>
      )}
    </div>
  );
}
